import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { P, RequirePermission } from '@/features/permissions';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
type Import = {
  id: string;
  status: string;
  match_status: string;
  error_code: string | null;
  source: { started_at: string; direction: string; external_phone: string | null };
};
export function QuoEncounterPanel({ encounterId }: { encounterId: string }) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const base = `/api/v1/organizations/${orgId}/quo`;
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false);
  const query = useQuery<Import[]>({
    enabled: !!orgId,
    queryKey: ['quo-import', orgId, encounterId],
    queryFn: async () => (await clientApi.get(`${base}/imports`, { params: { encounter_id: encounterId } })).data,
    refetchInterval: (query) =>
      query.state.data?.some((x) => ['pending', 'processing'].includes(x.status)) ? 5000 : false,
  });
  const item = query.data?.[0];
  useEffect(() => {
    if (item?.status === 'ready') cache.invalidateQueries({ queryKey: ['encounters', orgId, encounterId] });
  }, [item?.status, cache, orgId, encounterId]);
  if (!item) return null;
  async function act(action: string) {
    if (!item) return;
    setBusy(true);
    try {
      await clientApi.post(`${base}/imports/${item.id}/${action}`);
      // Targeted, not a bare invalidateQueries(). As delivered this dropped
      // every query in the app, so acting on one import refetched unrelated
      // patient and document lists. `['encounters', orgId, encounterId]`
      // prefix-matches the audio-segments key, which is what makes newly
      // imported recordings appear.
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['quo-import', orgId, encounterId] }),
        cache.invalidateQueries({ queryKey: ['encounters', orgId, encounterId] }),
      ]);
      toast.success(action === 'retry' ? 'Import queued' : 'Import dismissed');
    } catch {
      toast.error('Unable to change this import. Only draft encounters can be changed.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Badge variant="secondary">Quo</Badge> Telephone recording
        </CardTitle>
        <CardDescription>
          {item.source.direction === 'incoming' ? 'Incoming' : 'Outgoing'} call ·{' '}
          {new Date(item.source.started_at).toLocaleString()} ·{' '}
          {item.source.external_phone ?? 'Caller number unavailable'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <p>Audio import: {item.status.replaceAll('_', ' ')}</p>
        <p>
          Initial patient match:{' '}
          {item.match_status === 'manual'
            ? 'Patient selected by staff.'
            : item.match_status === 'phone_unique'
              ? 'Automatically attached by a unique phone match. You can correct the patient below.'
              : 'No unique phone match. You can generate a draft summary now and match the patient from Inbox afterward.'}
        </p>
        <p>Choose a document template and add details before submitting for EMR transcription.</p>
        {item.error_code && (
          <p role="status">
            Import needs attention: {item.error_code.replaceAll('_', ' ')}. Your administrator can check the connection.
          </p>
        )}
      </CardContent>
      <CardFooter className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => {
            query.refetch();
            cache.invalidateQueries({ queryKey: ['encounters', orgId, encounterId] });
          }}>
          Refresh
        </Button>
        <RequirePermission permission={P.UPDATE_ENCOUNTER}>
          <Button variant="outline" disabled={busy || item.status === 'processing'} onClick={() => act('retry')}>
            Retry audio import
          </Button>
          <Button disabled={busy} variant="ghost" onClick={() => act('dismiss')}>
            Dismiss import
          </Button>
        </RequirePermission>
      </CardFooter>
    </Card>
  );
}
